import {Trans, useTranslation} from '@/translations/i18n.tsx'
import {
    ModalBody,
    ModalCloseButton,
    ModalFooter,
    ModalHeader,
    ModalParagraph,
    ModalTitle
} from '@components/ui/modal-atoms'
import {Dialog, DialogClose, DialogContent} from '@components/ui/dialog'
import {FC, useState} from 'react'
import {Button} from '@components/ui/button'
import {TextArea} from '@components/commons/textarea'
import {InputHelpText} from '@components/ui/input-help-text'
import {useChangeFreelancerDescription} from '@features/settings/queries/useChangeFreelancerDescription.ts'
import {Spinner} from '@components/ui/spinner'

interface ChangeDescriptionModalProps {
    onClose: () => void
    userDescription: string | null
    userId: number
}

const DESCRIPTION_MAX_LENGTH = 80

export const ChangeDescriptionModal: FC<ChangeDescriptionModalProps> = ({onClose, userDescription, userId}) => {
    const {t} = useTranslation()
    const [textareaValue, setTextareaValue] = useState(userDescription ?? '')
    const changeFreelancerDescriptionMutation = useChangeFreelancerDescription({userId, onSuccess: onClose})

    const hasDescriptionLengthError = textareaValue.length > DESCRIPTION_MAX_LENGTH

    const handleSubmit = () => {
        changeFreelancerDescriptionMutation.mutate({description: textareaValue})
    }
    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent
                className="w-120 grid grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden"
                showCloseButton={false}
            >
                <ModalHeader>
                    <DialogClose data-slot="dialog-close" render={<ModalCloseButton />} />
                </ModalHeader>
                <ModalBody>
                    <div className="flex flex-col gap-8">
                        <div className="flex flex-col gap-2">
                            <ModalTitle>{t('settings:personal_information:intro_modal_title')}</ModalTitle>
                            <ModalParagraph>{t('settings:personal_information:intro_modal_text')}</ModalParagraph>
                        </div>
                        <div className="flex flex-col gap-1.5 w-full">
                            <TextArea
                                defaultValue={userDescription ?? ''}
                                disabled={changeFreelancerDescriptionMutation.isPending}
                                rows={5}
                                onChange={event => setTextareaValue(event.target.value)}
                                errorMessage={
                                    hasDescriptionLengthError && (
                                        <Trans
                                            i18nKey="settings:personal_information:characters_over_limit"
                                            components={{strong: <strong />}}
                                            values={{
                                                count: textareaValue.length - DESCRIPTION_MAX_LENGTH
                                            }}
                                        />
                                    )
                                }
                                helpText={
                                    <InputHelpText
                                        helpText={
                                            <Trans
                                                i18nKey="settings:personal_information:characters_left"
                                                components={{strong: <strong />}}
                                                values={{
                                                    count: DESCRIPTION_MAX_LENGTH - textareaValue.length
                                                }}
                                            />
                                        }
                                    />
                                }
                            />
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button
                        className="ml-auto"
                        onClick={handleSubmit}
                        disabled={
                            changeFreelancerDescriptionMutation.isPending ||
                            !!hasDescriptionLengthError ||
                            textareaValue == userDescription
                        }
                    >
                        {t('commons:save')}
                        {changeFreelancerDescriptionMutation.isPending && <Spinner />}
                    </Button>
                </ModalFooter>
            </DialogContent>
        </Dialog>
    )
}
